/**
 * RichTextContent Component
 *
 * Reusable component for rendering Contentful Rich Text fields.
 * Converts Rich Text JSON to HTML and renders via Markup component.
 * Supports embedded entries and assets via bridge components.
 *
 * Usage:
 *   import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
 *
 *   <RichTextContent content={component.blogContent} />
 *   <RichTextContent content={component.description} className="custom-class" />
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {BLOCKS, INLINES} from '@contentful/rich-text-types';
import {documentToHtmlString} from '@contentful/rich-text-html-renderer';

import {Markup} from '@amblique/base/app/components/shared/markup';

/**
 * Build lookup maps from Rich Text links.
 * Only published/resolved entries and assets are included; unpublished or missing
 * references are skipped so they can be omitted at render time and other embeds still display.
 *
 * @param {Object} links - Rich Text links {entries, assets}
 * @returns {Object} {entryMap, assetMap}
 */
const buildLinkMaps = (links) => {
  const entryMap = new Map();
  const assetMap = new Map();

  const entryBlocks = links?.entries?.block ?? [];
  const assetBlocks = links?.assets?.block ?? [];

  entryBlocks.forEach((entry) => {
    if (entry?.sys?.id) {
      entryMap.set(entry.sys.id, entry);
    }
  });

  assetBlocks.forEach((asset) => {
    if (asset?.sys?.id) {
      assetMap.set(asset.sys.id, asset);
    }
  });

  return {entryMap, assetMap};
};

/**
 * Escape HTML attribute value
 *
 * @param {string} str - String to escape
 * @returns {string}
 */
const escapeAttr = (str) => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Options for `documentToHtmlString` (embedded nodes + optional hyperlink override).
 *
 * @param {Map} entryMap
 * @param {Map} assetMap
 * @param {(uri: string) => string | null | undefined} [hyperlinkHrefTransform] - `null` drops the anchor; `undefined` keeps `uri`.
 * @returns {Object} render options
 */
const createRenderOptions = (entryMap = new Map(), assetMap = new Map(), hyperlinkHrefTransform) => {
  const renderNode = {
    // Embedded Entry Block
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const entryId = node?.data?.target?.sys?.id;

      if (!entryId) {
        console.warn('[RichTextContent] Embedded entry missing ID');

        return '';
      }

      const entry = entryMap.get(entryId);

      if (entry) {
        const componentJson = escapeAttr(JSON.stringify(entry));

        return `<div role="embedded-entry-block" data-id="${entryId}" data-component="${componentJson}"></div>`;
      }

      // No entry data (e.g. draft or unpublished) - hide placeholder so draft embeds are not shown
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[RichTextContent] Embedded entry not available (draft/unpublished): ${entryId}. Skipping render.`);
      }

      return '';
    },

    // Embedded Asset Block
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const assetId = node?.data?.target?.sys?.id;

      if (!assetId) {
        console.warn('[RichTextContent] Embedded asset missing ID');

        return '';
      }

      const asset = assetMap.get(assetId);

      if (asset) {
        const assetJson = escapeAttr(JSON.stringify(asset));

        return `<div role="embedded-asset-block" data-id="${assetId}" data-asset="${assetJson}"></div>`;
      }

      // No asset data (e.g. draft or unpublished) - hide placeholder
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[RichTextContent] Embedded asset not available (draft/unpublished): ${assetId}. Skipping render.`);
      }

      return '';
    },
  };

  if (hyperlinkHrefTransform) {
    renderNode[INLINES.HYPERLINK] = (node, next) => {
      const rawUri = node.data.uri || '';
      const mapped = hyperlinkHrefTransform(rawUri);

      if (mapped === null) {
        return next(node.content);
      }

      const href = mapped ?? rawUri;

      return `<a href="${escapeAttr(href)}">${next(node.content)}</a>`;
    };
  }

  return {renderNode};
};

/**
 * Convert Contentful Rich Text to HTML string with embedded entry/asset support
 *
 * Embedded entries are rendered as: <div role="embedded-entry-block" data-id="..." data-component="..."></div>
 * Embedded assets are rendered as: <div role="embedded-asset-block" data-id="..." data-asset="..."></div>
 *
 * These placeholders are then converted to React components by jsxParser via Markup component.
 *
 * @param {Object} richTextField - Rich text field from Contentful {json, links}
 * @param {(uri: string) => string | null | undefined} [hyperlinkHrefTransform]
 * @returns {string|null}
 */
export const richTextToHtml = (richTextField, hyperlinkHrefTransform) => {
  if (!richTextField?.json) return null;

  const {json, links} = richTextField;

  // Build lookup maps (empty when no links); always use render options so embedded entries/assets
  // are handled and unpublished/missing embeds are skipped while others still display
  const {entryMap, assetMap} = buildLinkMaps(links || {});
  const options = createRenderOptions(entryMap, assetMap, hyperlinkHrefTransform);

  return documentToHtmlString(json, options);
};

/**
 * RichTextContent Component
 *
 * Renders Contentful Rich Text field as HTML via Markup component.
 * Supports embedded entries and assets via bridge components in jsxParser.
 *
 * @param {Object} props - Component props
 * @param {Object} props.content - Rich text field from Contentful {json, links}
 * @param {React.ElementType} [props.as] - Element type to render as
 * @param {string} [props.className] - Additional CSS classes
 * @param {(uri: string) => string | null | undefined} [props.hyperlinkHrefTransform]
 * @returns {React.ReactElement|null}
 */
export const RichTextContent = ({content, as, className = '', hyperlinkHrefTransform, ...props}) => {
  const html = useMemo(
    () => richTextToHtml(content, hyperlinkHrefTransform),
    [content, hyperlinkHrefTransform]
  );

  if (!html) return null;

  return (
    <Markup
      as={as}
      className={`rich-text-content ${className}`.trim()}
      source={html}
      {...props}
    />
  );
};

RichTextContent.displayName = 'RichTextContent';
RichTextContent.propTypes = {
  content: PropTypes.shape({
    json: PropTypes.object,
    links: PropTypes.shape({
      entries: PropTypes.shape({
        block: PropTypes.array,
      }),
      assets: PropTypes.shape({
        block: PropTypes.array,
      }),
    }),
  }),
  as: PropTypes.elementType,
  className: PropTypes.string,
  hyperlinkHrefTransform: PropTypes.func,
};

export default RichTextContent;
